import React, { useState } from "react";
import { Card, Layout, Collapse, Button, Form, Input, Space, Radio, Upload } from "antd";
import { CloseOutlined, MinusCircleOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import PageTitle from "components/PageTitle";
import DashboardLayout from "layout/DashboardLayout";

const { Content } = Layout;
const { TextArea } = Input;

type ItemType = {
  key: string;
  label: string;
  children: JSX.Element;
};

function PageItem() {
  const [items, setItems] = useState<ItemType[]>([
    {
      key: "1",
      label: "Page section 1",
      children: <SectionForm onRemove={() => removeSection("1")} />,
    },
  ]);

  const addSection = () => {
    const newKey = (items.length + 1).toString();
    const newSection = {
      key: newKey,
      label: `Page section ${newKey}`,
      children: <SectionForm onRemove={() => removeSection(newKey)} />,
    };
    setItems([...items, newSection]);
  };

  const removeSection = (key: string) => {
    setItems(items.filter((item) => item.key !== key));
  };

  const saveSection = (key: string) => {
    // Add save logic here, e.g., saving form data
    console.log(`Saving section ${key}`);
  };

  return (
    <DashboardLayout>
      <Layout>
        <PageTitle title="Page Content Builder" pageTitle={[{ title: "page" }]} />
        <Content style={{ margin: 24 }}>
          <Card bordered className="border-neutral-200 mb-8 text-lg font-bold">
            Use this panel to build and customize page sections. Add and organize sections, cards, and pricing options
            for your page content. Define each section’s rank, name, type, headings, and upload relevant images. Utilize
            the &lsquo;Add Button&rsquo;, &lsquo;Add Card&rsquo;, and &lsquo;Add Payment Card&rsquo; options to enhance
            content layout and engage users effectively. Save individual sections or save all changes at once.
          </Card>
          <Collapse accordion>
            {items.map((item) => (
              <Collapse.Panel
                key={item.key}
                header={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{item.label}</span>
                    <div>
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => saveSection(item.key)}
                        style={{ marginRight: 8 }}
                      >
                        Save
                      </Button>
                      <Button size="small" danger onClick={() => removeSection(item.key)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                }
              >
                {item.children}
              </Collapse.Panel>
            ))}
          </Collapse>

          <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
            <Button type="primary" style={{ marginRight: 8 }}>
              Save All
            </Button>
            <Button onClick={addSection}>Add Section</Button>
          </div>
        </Content>
      </Layout>
    </DashboardLayout>
  );
}

// Define the props type for SectionForm
interface SectionFormProps {
  onRemove: () => void; // Define the type for onRemove
}
const SectionForm: React.FC<SectionFormProps> = ({ onRemove }) => {
  return (
    <Form layout="vertical" style={{ padding: 16 }}>
      <Space style={{ width: "70%", marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
        <Form.Item
          label="Section Rank"
          name="sectionRank"
          style={{ flex: 1 }}
          labelCol={{ style: { fontWeight: "bold" } }}
        >
          <Input type="number" placeholder="Enter section rank" />
        </Form.Item>
        <Form.Item
          label="Section Name"
          name="sectionName"
          style={{ flex: 2 }}
          labelCol={{ style: { fontWeight: "bold" } }}
        >
          <Input placeholder="Enter section name" />
        </Form.Item>
        <Form.Item
          label="Section Type"
          name="sectionType"
          style={{ flex: 1 }}
          labelCol={{ style: { fontWeight: "bold" } }}
        >
          <Radio.Group>
            <Radio value="Card">Card</Radio>
            <Radio value="Section">Section</Radio>
            <Radio value="Pricing">Pricing</Radio>
          </Radio.Group>
        </Form.Item>
      </Space>

      <Space style={{ width: "70%", marginBottom: 16, display: "flex", gap: 25 }}>
        <Form.Item label="Heading" name="heading" style={{ flex: 1 }} labelCol={{ style: { fontWeight: "bold" } }}>
          <Input placeholder="Enter heading" style={{ height: 48, width: 475 }} />
        </Form.Item>
        <Form.Item
          label="Sub Heading"
          name="subHeading"
          style={{ flex: 1 }}
          labelCol={{ style: { fontWeight: "bold" } }}
        >
          <TextArea
            showCount
            maxLength={100}
            allowClear
            placeholder="Enter sub-heading"
            style={{ height: 48, width: 475 }}
            size="large"
          />
        </Form.Item>
      </Space>

      <Form.Item label="Upload Images For the Section" name="images" labelCol={{ style: { fontWeight: "bold" } }}>
        <Upload listType="picture" maxCount={5}>
          <Button icon={<UploadOutlined />}>Upload Images</Button>
        </Upload>
      </Form.Item>

      <Form.List name="buttons">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                <Form.Item {...restField} name={[name, "first"]}>
                  <Input placeholder="Button Content" />
                </Form.Item>
                <Form.Item {...restField} name={[name, "last"]}>
                  <Input placeholder="Button Action URL" />
                </Form.Item>
                <MinusCircleOutlined onClick={() => remove(name)} />
              </Space>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add Button
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <Form.List name="cards">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Space key={key} style={{ display: "flex", marginBottom: 8, border: "1px solid #ddd" }} align="baseline">
                <Form.Item {...restField} name={[name, "Card Icon"]}>
                  <Input placeholder="Card Icon" />
                </Form.Item>
                <Form.Item {...restField} name={[name, "Card Heading"]}>
                  <Input placeholder="Card Heading" />
                </Form.Item>
                <Form.Item {...restField} name={[name, "Card subHeading"]}>
                  <Input placeholder="Card SubHeading" />
                </Form.Item>
                <MinusCircleOutlined onClick={() => remove(name)} />
              </Space>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add Card
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <Form.List name="payment card">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key} style={{ marginBottom: 16, border: "1px solid #ddd", padding: 16, borderRadius: 8 }}>
                <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                  <Form.Item {...restField} name={[name, "Icon"]}>
                    <Input placeholder="Icon" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "Heading"]}>
                    <Input placeholder="Heading" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "SubHeading"]}>
                    <Input placeholder="SubHeading" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "Pricing Frequency"]}>
                    <Input placeholder="Pricing Frequency" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "Pricing Type"]}>
                    <Input placeholder="Pricing Type" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "Total Price"]}>
                    <Input placeholder="Total Price" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "Discounted Price"]}>
                    <Input placeholder="Discounted Price" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "Discount"]}>
                    <Input placeholder="Discount" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
                <Form.List name={[name, "features"]}>
                  {(featureFields, { add: addFeature, remove: removeFeature }) => (
                    <>
                      {featureFields.map(({ key: featureKey, name: featureName, ...featureRestField }) => (
                        <Space key={featureKey} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                          <Form.Item
                            {...featureRestField}
                            name={[featureName, "Feature Description"]}
                            rules={[{ required: true, message: "Please enter feature description" }]}
                          >
                            <Input placeholder="Feature Description" />
                          </Form.Item>
                          <MinusCircleOutlined onClick={() => removeFeature(featureName)} />
                        </Space>
                      ))}
                      <Form.Item>
                        <Button type="dashed" onClick={() => addFeature()} block icon={<PlusOutlined />}>
                          Add Feature
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>

                <Form.Item>
                  <Space>
                    <Form.Item {...restField} name={[name, "first"]}>
                      <Input placeholder="Button Content" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, "last"]}>
                      <Input placeholder="Button Action URL" />
                    </Form.Item>
                  </Space>
                </Form.Item>
              </div>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add Payment Card
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
      <Button type="default" danger onClick={onRemove}>
        Remove Section
      </Button>
    </Form>
  );
};

export default PageItem;
